<script setup lang="ts">
import APLogoTypeRow from '~~/public/img/Airalo-Partners-logotype-row.svg?component'

const route = useRoute()

const { attributes = {} } = defineProps<TBuilderBaseProps>()

const { locale, localeIsoCode } = useLocaleIso()

const { data: headerLinkList } = await useAsyncData(
  `header-nav-links-${route.path}`,
  () => {
    return $fetch<(TLink | TSubMenuItem)[]>(
      `/api/header-navigation-links/${localeIsoCode.value}`,
      {
        method: 'post',
      }
    )
  },
  {
    default: () => [],
    watch: [locale],
    lazy: true,
  }
)

const { data: commonLinksList } = useNuxtData<TLink[]>('global-common-links')

const signInLink = computed(() => {
  return (commonLinksList?.value ?? []).find((link) => link.text === 'Sign in')
})

const stateMobileMenuOpen = useState('mobile-menu-opened', () => false)

watch(stateMobileMenuOpen, (value) => {
  setPageLayout(value ? 'no-scroll' : 'default')
})

onBeforeRouteLeave(() => {
  stateMobileMenuOpen.value = false
  setPageLayout('default')
})
</script>

<template>
  <div
    v-bind="attributes"
    class="w-full px-4 lg:px-24 py-1 lg:py-4 bg-white justify-between items-center inline-flex relative z-10"
  >
    <div
      class="justify-start md:justify-between lg:justify-start items-center gap-8 inline-flex w-full"
    >
      <NuxtLink to="/" data-testid="header-logo-link">
        <APLogoTypeRow class="h-auto lg:h-8 w-auto" loading="lazy" />
      </NuxtLink>

      <div
        class="h-full justify-start items-center gap-2 hidden md:inline-flex"
        data-testid="header-nav-desktop"
      >
        <div
          v-for="(item, index) in headerLinkList"
          :key="`header-menu-links-${'title' in item ? item.title : item.text}-${index}`"
          class="h-full justify-start items-center gap-1 inline-flex"
        >
          <LazyBaseHeaderMenuItem
            :title="'text' in item ? item.text : item.title"
            :url="'url' in item ? item.url : undefined"
            :sub-menu-items="'links' in item ? item : null"
          />
        </div>
      </div>
    </div>

    <div class="justify-end items-center gap-4 inline-flex">
      <div
        class="p-2 flex-col justify-start items-start gap-4 lg:gap-2 hidden md:inline-flex"
      >
        <div class="justify-end items-center gap-4 inline-flex">
          <slot />
        </div>
      </div>

      <!--Mobile-->
      <div class="p-1 inline-flex justify-between gap-2 md:hidden">
        <div class="hidden lg:inline-flex sm:hidden">
          <slot />
        </div>

        <div v-if="signInLink">
          <a :href="signInLink.url" aria-label="Sign in">
            <TrailDSButton variant="tertiary">
              {{ signInLink.text }}
            </TrailDSButton>
          </a>
        </div>

        <div
          class="inline-flex justify-center items-center gap-1"
          @click="stateMobileMenuOpen = !stateMobileMenuOpen"
        >
          <NuxtIcon
            name="heroicons:chevron-down"
            size="1.5rem"
            :class="[
              'will-change-transform duration-200',
              {
                'rotate-180': stateMobileMenuOpen,
              },
            ]"
          />

          <div
            class="text-trail-text-title-normal text-trail-title4"
            aria-label="Menu button"
          >
            Menu
          </div>
        </div>

        <TransitionExpand
          mode="out-in"
          :duration="{ enter: 200, leave: 400 }"
          axis="y"
          tag="div"
          no-opacity
          group
          appear
        >
          <div
            v-show="stateMobileMenuOpen"
            class="overflow-y-scroll absolute left-0 top-16 md:top-20 w-[100dvw] h-dvh md:h-dvh max-h-[100dvh] z-10 bg-white px-4 py-8 flex-col justify-start items-start gap-4 inline-flex"
          >
            <div
              v-if="headerLinkList?.length"
              class="w-full flex-col justify-start items-start gap-4 flex"
            >
              <div
                v-for="(menuItem, index) in headerLinkList"
                :key="`header-menu-links-${'title' in menuItem ? menuItem.title : menuItem.text}-${index}`"
                class="w-full justify-start items-start gap-1 inline-flex"
              >
                <LazyBaseHeaderMenuItem
                  :title="'text' in menuItem ? menuItem.text : menuItem.title"
                  :url="'url' in menuItem ? menuItem.url : undefined"
                  :sub-menu-items="'links' in menuItem ? menuItem : null"
                  :options="{ mobile: true, relative: false }"
                />
              </div>
            </div>

            <div class="inline-flex lg:hidden sm:hidden w-full pb-12">
              <slot />
            </div>
          </div>
        </TransitionExpand>
      </div>
    </div>
  </div>
</template>
